import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getProfile, saveExternalCert, deleteExternalCert, submitEditRequest, GetProfileOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Trash2, ShieldCheck, User, TrendingUp, Award, Briefcase, ClipboardList, Pencil, Info, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import SkillBadges from '@/components/dashboard/SkillBadges';
import AssessorPinSetup from '@/components/training/AssessorPinSetup';

type Profile = GetProfileOutputType;
type ExtCert = GetProfileOutputType['externalCerts'][0];

const certTypes = ['Food Hygiene Level 2', 'Allergen Awareness', 'Personal Licence', 'First Aid', 'Other'];

const blankCert = { certificateName: '', certificateType: '', issuedDate: '', expiryDate: '', notes: '' };

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };

const EDITABLE_FIELDS = [
  { label: 'Preferred Name', key: 'preferredName' },
  { label: 'Address Line 1', key: 'addressLine1' },
  { label: 'Address Line 2', key: 'addressLine2' },
  { label: 'Town', key: 'town' },
  { label: 'County', key: 'county' },
  { label: 'Postcode', key: 'postcode' },
  { label: 'Emergency Contact Name', key: 'emergencyContactName' },
  { label: 'Emergency Contact Relationship', key: 'emergencyContactRelationship' },
  { label: 'Emergency Contact Phone', key: 'emergencyContactPhone' },
  { label: 'Emergency Alt Phone', key: 'emergencyAltPhone' },
  { label: 'Bank Account Holder', key: 'bankAccountHolder' },
  { label: 'Bank Name', key: 'bankName' },
  { label: 'Sort Code', key: 'sortCode' },
  { label: 'Account Number', key: 'accountNumber' },
];

const BANK_FIELDS = ['Bank Account Holder', 'Bank Name', 'Sort Code', 'Account Number'];

export default function Profile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [certForm, setCertForm] = useState({ ...blankCert });
  const [savingCert, setSavingCert] = useState(false);

  // Edit request state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFieldName, setEditFieldName] = useState('');
  const [editNewValue, setEditNewValue] = useState('');
  const [submittingEdit, setSubmittingEdit] = useState(false);

  const loadProfile = () => {
    if (!user) return;
    getProfile({}).then(setProfile).finally(() => setLoading(false));
  };

  useEffect(() => { loadProfile(); }, [user]);

  const openAddCert = () => { setCertForm({ ...blankCert }); setCertDialogOpen(true); };

  const saveCert = async () => {
    if (!certForm.certificateName.trim()) { toast.error('Certificate name is required'); return; }
    setSavingCert(true);
    try {
      await saveExternalCert({ ...certForm });
      toast.success('Certificate saved');
      setCertDialogOpen(false);
      loadProfile();
    } catch { toast.error('Failed to save'); }
    finally { setSavingCert(false); }
  };

  const deleteCert = async (id: string) => {
    try {
      await deleteExternalCert({ id });
      toast.success('Certificate removed');
      setProfile(p => p ? { ...p, externalCerts: p.externalCerts.filter(c => c.id !== id) } : p);
    } catch { toast.error('Failed to delete'); }
  };

  const openEditRequest = (fieldLabel: string) => {
    const fieldDef = EDITABLE_FIELDS.find(f => f.label === fieldLabel);
    if (!fieldDef) return;
    const currentValue = (user as any)?.[fieldDef.key] || '';
    setEditFieldName(fieldLabel);
    setEditNewValue(currentValue);
    setEditDialogOpen(true);
  };

  const submitEdit = async () => {
    if (!editNewValue.trim()) { toast.error('Please enter the new value'); return; }
    setSubmittingEdit(true);
    try {
      const fieldDef = EDITABLE_FIELDS.find(f => f.label === editFieldName);
      const oldValue = fieldDef ? (user as any)?.[fieldDef.key] || '' : '';
      await submitEditRequest({ fieldName: editFieldName, oldValue, newValue: editNewValue.trim() });
      toast.success('Change request submitted — pending admin approval');
      setEditDialogOpen(false);
    } catch (e: any) { toast.error(e.message || 'Failed to submit'); }
    finally { setSubmittingEdit(false); }
  };

  const maskValue = (label: string, value: string) => {
    if (label === 'Account Number' && value.length > 4) return '••••' + value.slice(-4);
    if (label === 'Sort Code' && value.length > 2) return '••' + value.slice(-2);
    return value;
  };

  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`;
  const pct = profile ? Math.round((profile.stats.modulesCompleted / Math.max(profile.stats.totalModules, 1)) * 100) : 0;

  if (loading) {
    return (
      <div className="px-6 md:px-8 py-8 max-w-5xl mx-auto space-y-6">
        <Skeleton className="h-24" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="px-6 md:px-8 py-6 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
            <span className="text-xl font-bold text-primary">{initials || '?'}</span>
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">
              {user?.firstName} {user?.lastName}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {user?.department || 'No department'} · {user?.role || 'Staff'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 md:px-8 py-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Personal Details */}
          <div className="border border-border bg-card rounded-md p-5">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-foreground text-sm">Personal Details</h3>
            </div>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email</span>
                <span className="text-foreground truncate ml-2 max-w-36">{user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Department</span>
                <span className="text-foreground">{user?.department || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Role</span>
                <span className="text-foreground">{user?.role || '—'}</span>
              </div>
              {user?.position && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Position</span>
                  <span className="text-foreground">{user.position}</span>
                </div>
              )}
              {user?.employmentType && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contract</span>
                  <span className="text-foreground">{user.employmentType}</span>
                </div>
              )}
              {user?.startDate && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Start Date</span>
                  <span className="text-foreground">{fmtDate(user.startDate)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Training Progress */}
          <div className="border border-border bg-card rounded-md p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-foreground text-sm">Training Progress</h3>
            </div>
            {profile ? (
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-muted-foreground">Modules completed</span>
                    <span className="text-foreground font-medium">{profile.stats.modulesCompleted} / {profile.stats.totalModules}</span>
                  </div>
                  <Progress value={pct} className="h-2" />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Quizzes passed</span>
                  <span className="text-foreground font-medium">{profile.stats.quizzesPassed}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Awaiting sign-off</span>
                  <span className="text-foreground font-medium">{profile.stats.awaitingSignoff}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No progress data yet.</p>
            )}
          </div>

          {/* External Certifications */}
          <div className="border border-border bg-card rounded-md p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-foreground text-sm">External Certifications</h3>
              </div>
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={openAddCert}>
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
            {profile && profile.externalCerts.length > 0 ? (
              <div className="space-y-3">
                {profile.externalCerts.map(cert => (
                  <div key={cert.id} className="flex items-start justify-between gap-2 border-b border-border pb-2.5 last:border-0 last:pb-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <ShieldCheck className="w-3.5 h-3.5 text-primary shrink-0" />
                        <p className="text-sm font-medium text-foreground truncate">{cert.certificateName}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">{cert.certificateType}</p>
                      <p className="text-xs text-muted-foreground">
                        Issued {fmtDate(cert.issuedDate)}
                        {cert.expiryDate ? ` · Expires ${fmtDate(cert.expiryDate)}` : ' · No expiry'}
                      </p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0 text-destructive hover:text-destructive">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Remove "{cert.certificateName}"?</AlertDialogTitle></AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteCert(cert.id)}>Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No external certifications recorded yet.</p>
            )}
          </div>
        </div>
      </div>

      {/* Skills + Onboarding shortcut */}
      <div className="px-6 md:px-8 pb-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-border bg-card rounded-md p-5">
            <div className="flex items-center gap-2 mb-3">
              <Briefcase className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-foreground text-sm">Skills & Capabilities</h3>
            </div>
            <SkillBadges skills={(user?.skills as string[]) || []} />
          </div>
          <div className="border border-border bg-card rounded-md p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-foreground text-sm">Onboarding</h3>
              </div>
              <Link to="/onboarding" className="text-xs text-primary hover:underline">Open →</Link>
            </div>
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="text-muted-foreground">Status</span>
              <span className={`text-sm font-medium ${user?.onboardingStatus === 'Approved' ? 'text-green-500' : user?.onboardingStatus === 'Submitted' ? 'text-blue-400' : 'text-amber-400'}`}>{user?.onboardingStatus || 'Not Started'}</span>
            </div>
            <Link to="/documents" className="text-xs text-muted-foreground hover:text-foreground mt-2 block">View my documents →</Link>
          </div>
        </div>
      </div>

      {/* Assessor PIN (Managers+ only) */}
      {user?.role && ['Manager', 'Supervisor', 'Admin', 'Owner'].includes(user.role) && (
        <div className="px-6 md:px-8 pb-6 max-w-5xl mx-auto">
          <AssessorPinSetup />
        </div>
      )}

      {/* Assessor PIN (Managers+ only) */}
      {user?.role && ['Manager', 'Supervisor', 'Admin', 'Owner'].includes(user.role) && (
        <div className="px-6 md:px-8 pb-6 max-w-5xl mx-auto">
          <AssessorPinSetup />
        </div>
      )}

      {/* My Details Section */}
      <div className="px-6 md:px-8 pb-6 max-w-5xl mx-auto">
        <div className="border border-border bg-card rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-foreground text-sm">My Details</h3>
            </div>
            <Button size="sm" variant="outline" className="h-7 px-3 text-xs" onClick={() => setEditDialogOpen(true)}>
              <Pencil className="w-3 h-3 mr-1.5" /> Request a Change
            </Button>
          </div>

          <div className="flex items-start gap-2 bg-muted/40 rounded px-3 py-2 text-xs text-muted-foreground mb-4">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary" />
            <span>
              <strong className="text-foreground">Changing your details here does not update them automatically.</strong>{' '}
              All changes require office approval. You'll receive an email once approved.
              Bank detail changes are verified separately.
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {[
              { label: 'Full Name', value: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() },
              { label: 'Date of Birth', value: user?.dateOfBirth ? fmtDate(user.dateOfBirth) : '—' },
              { label: 'NI Number', value: (() => {
                const ni = user?.niNumber;
                if (!ni) return '—';
                const r = user?.role;
                if (r === 'Owner' || r === 'Admin') return ni;
                return '•••••' + ni.slice(-3);
              })() },
              { label: 'Position', value: user?.position || '—' },
              { label: 'Address', value: [user?.addressLine1, user?.addressLine2, user?.town, user?.county, user?.postcode].filter(Boolean).join(', ') || '—' },
              { label: 'Emergency Contact', value: user?.emergencyContactName ? `${user.emergencyContactName}${user.emergencyContactRelationship ? ` (${user.emergencyContactRelationship})` : ''}${user.emergencyContactPhone ? ` — ${user.emergencyContactPhone}` : ''}` : '—' },
              { label: 'Bank Account', value: user?.accountNumber ? `${user.bankName ? user.bankName + ' · ' : ''}••••${user.accountNumber.slice(-4)}` : '—' },
              { label: 'Sort Code', value: user?.sortCode ? '••-••-' + user.sortCode.replace(/[^0-9]/g, '').slice(-2) : '—' },
            ].map(row => (
              <div key={row.label} className="flex justify-between border-b border-border pb-2.5 last:border-0 last:pb-0">
                <span className="text-muted-foreground">{row.label}</span>
                <span className="text-foreground text-right ml-4">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add Cert Dialog */}
      <Dialog open={certDialogOpen} onOpenChange={setCertDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add External Certification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Certificate Name *</label>
              <Input value={certForm.certificateName} onChange={e => setCertForm(f => ({ ...f, certificateName: e.target.value }))} placeholder="e.g. Food Hygiene Level 2" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Certificate Type</label>
              <Select value={certForm.certificateType || 'none'} onValueChange={v => setCertForm(f => ({ ...f, certificateType: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not specified</SelectItem>
                  {certTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Issued Date</label>
                <Input type="date" value={certForm.issuedDate} onChange={e => setCertForm(f => ({ ...f, issuedDate: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Expiry Date</label>
                <Input type="date" value={certForm.expiryDate} onChange={e => setCertForm(f => ({ ...f, expiryDate: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Notes</label>
              <Input value={certForm.notes} onChange={e => setCertForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" />
            </div>
            <Button onClick={saveCert} disabled={savingCert} className="w-full bg-primary text-primary-foreground">
              {savingCert ? 'Saving...' : 'Save Certificate'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Request Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request a Change</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded px-3 py-2 text-xs text-amber-600">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>This is a change request — it must be approved by the office before it takes effect. Bank detail changes will be verified separately.</span>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Field to Change *</label>
              <Select value={editFieldName || 'none'} onValueChange={v => {
                if (v !== 'none') {
                  const fieldDef = EDITABLE_FIELDS.find(f => f.label === v);
                  setEditFieldName(v);
                  setEditNewValue(fieldDef ? (user as any)?.[fieldDef.key] || '' : '');
                }
              }}>
                <SelectTrigger><SelectValue placeholder="Select field" /></SelectTrigger>
                <SelectContent>
                  {EDITABLE_FIELDS.map(f => (
                    <SelectItem key={f.key} value={f.label}>
                      {f.label}{BANK_FIELDS.includes(f.label) ? ' ⚠' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editFieldName && (
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">New Value *</label>
                <Input
                  value={editNewValue}
                  onChange={e => setEditNewValue(e.target.value)}
                  placeholder={`Enter new ${editFieldName.toLowerCase()}`}
                />
              </div>
            )}
            <Button onClick={submitEdit} disabled={submittingEdit || !editFieldName || !editNewValue.trim()} className="w-full bg-primary text-primary-foreground">
              {submittingEdit ? 'Submitting...' : 'Submit Change Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
